import { makeObservable, observable, computed, action } from "mobx";
import { TypeRecords, TYPE_ALIGN } from "../commons";
import { IDataRequest, IDataResponse, IHttpClient, STATUS_CODE } from "../providers/api";
import FileDownload from "js-file-download";
import { CSSProperties } from "react";

export interface ColumnDefinition {
  group?: string;
  field: string;
  headerName: string;
  width?: number;
  description?: string;
  hide?: boolean;
  sortable?: boolean;
  mask?: string;
  type?: TypeRecords;
  align?: TYPE_ALIGN;
  colorCell?: string;
  render?: (row: any, style?: CSSProperties) => React.ReactNode;
}

export interface GroupColumnDefinition {
  headerName: string;
  colSpan: number;
}

export class DataTableStore {
  _provider: IHttpClient | null;
  _loading: boolean;
  _rows: any;
  _row: any;
  _totalRows: number;
  _rowsPerPageOptions: number[];
  _pageSize: number;
  _page: number;
  _filter: string;
  _path: string;
  _includes: string[];
  _orderBy: string[];
  _columnsDefinitions: ColumnDefinition[];
  _fileNameExport: string;
  _enableExport: boolean;

  constructor() {
    makeObservable(this, {
      _path: observable,
      _columnsDefinitions: observable,
      _provider: observable,
      _loading: observable,
      _rows: observable,
      _row: observable,
      _totalRows: observable,
      _rowsPerPageOptions: observable,
      _pageSize: observable,
      _page: observable,
      _filter: observable,
      _includes: observable,
      _orderBy: observable,
      _fileNameExport: observable,
      _enableExport: observable,
      loading: computed,
      setLoading: action,
      rows: computed,
      setRows: action,
      row: computed,
      setRow: action,
      totalRows: computed,
      setTotalRows: action,
      rowsPerPageOptions: computed,
      setRowsPerPageOptions: action,
      pageSize: computed,
      setPageSize: action,
      page: computed,
      setPage: action,
      filter: computed,
      setFilter: action,
      provider: computed,
      orderBy: computed,
      setOrderBy: action,
      orderByList: computed,
      columnsDefinitions: computed,
      // CRUD ACTIONS
      list: action,
      create: action,
      update: action,
      delete: action,
      find: action,
      triggerFilter: action,
      setColumnsDefinition: action,
    });
    this._loading = true;
    this._rows = [];
    this._row = {};
    this._totalRows = 0;
    this._rowsPerPageOptions = [5, 10, 25, 50];
    this._pageSize = 5;
    this._page = 1;
    this._filter = "";
    this._path = "";
    this._includes = [];
    this._orderBy = [];
    this._provider = null;
    this._columnsDefinitions = [];
    this._fileNameExport = "Planilha.xlsx";
    this._enableExport = false;
  }

  get columnsDefinitions(): ColumnDefinition[] {
    return this._columnsDefinitions;
  }

  setColumnsDefinition(columnsDefinitions: ColumnDefinition[]): void {
    this._columnsDefinitions = columnsDefinitions;
  }

  get provider(): IHttpClient | null {
    return this._provider || null;
  }

  setProvider(provider: IHttpClient): void {
    this._provider = provider;
  }

  get fileNameExport(): string {
    return this._fileNameExport;
  }

  setFileNameExport(fileNameExport: string): void {
    this._fileNameExport = fileNameExport;
  }

  get enableExport(): boolean {
    return this._enableExport;
  }

  setEnableExport(enableExport: boolean): void {
    this._enableExport = enableExport;
  }

  get path(): string {
    return this._path;
  }

  setPath(path: string): void {
    this._path = path;
  }

  get loading(): boolean {
    return this._loading;
  }

  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  get rows(): any {
    return this._rows;
  }

  setRows(rows: any): void {
    this._rows = rows;
  }

  get row(): any {
    return this._row;
  }

  setRow(row: any): void {
    this._row = row;
  }

  get totalRows(): number {
    return this._totalRows;
  }

  setTotalRows(totalRows: number): void {
    this._totalRows = totalRows;
  }

  get rowsPerPageOptions(): number[] {
    return this._rowsPerPageOptions;
  }

  setRowsPerPageOptions(rowsPerPageOptions: number[]): void {
    this._rowsPerPageOptions = rowsPerPageOptions;
  }

  get pageSize(): number {
    return this._pageSize;
  }

  setPageSize(pageSize: number): void {
    this._pageSize = pageSize;
  }

  get page(): number {
    return this._page;
  }

  setPage(page: number): void {
    this._page = page;
  }

  get filter(): string {
    return this._filter;
  }

  setFilter(filter: string): void {
    this._filter = filter;
  }

  get includes(): string {
    return this._includes.length > 0
      ? `&includes=${this._includes.join(",")}`
      : "";
  }

  setIncludes(includes: string[]): void {
    this._includes = includes;
  }

  get orderBy(): string {
    return this._orderBy.length > 0
      ? `&orderby=${this._orderBy.join(",")}`
      : "";
  }

  get orderByList(): string[] {
    return this._orderBy;
  }

  setOrderBy(orderBy: string[]): void {
    this._orderBy = orderBy;
  }

  async list(): Promise<void> {
    const response = await this?._provider?.get({
      url: `${this.path}?page=${this.page}&limit=${this.pageSize}${this.filter}${this.includes}${this.orderBy}`,
    });
    this.setRows(response?.data?.rows || []);
    this.setTotalRows(response?.data?.infoRows?.count || 0);
  }

  async export(): Promise<void> {
    const response = await this?._provider?.getFile({
      url: `${this.path}?format=xls&${this.filter}${this.includes}${this.orderBy}`,
    });

    if (response?.statusCode === STATUS_CODE.OK && response?.data) {
      FileDownload(response.data, this.fileNameExport, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    }
  }

  async find(primaryKey: string | number, includes: string[] = []): Promise<any> {
    const _includes = includes.length > 0 ? `/?includes=${includes.join(",")}` : "";

    const requestConfig: IDataRequest = {
      url: `${this.path}/${primaryKey}${_includes}`,
    };

    const response: IDataResponse | undefined = await this.provider?.get(requestConfig);

    if (response?.statusCode === STATUS_CODE.OK) {
      return response.data.rows.length > 0 ? response.data.rows[0] : null;
    }

    return null;
  }

  async create(): Promise<void> { }

  async update(row: any): Promise<boolean> {
    let updated: boolean = false;
    const response = await this.provider?.put({
      url: `${this.path}/${row.id}`,
      data: row,
    });

    if (parseInt(`${response?.statusCode}`) === 200) {
      updated = true;
    }
    return updated;
  }

  async delete(id: number): Promise<boolean> {
    let deleted = false;

    const response = await this?._provider?.delete({
      url: `${this.path}/${id}`,
    });

    if (parseInt(`${response?.statusCode}`) === 200) {
      deleted = true;
    }

    return deleted;
  }

  triggerFilter(value: string): void {
    let query = "";
    if (value) {
      query = `&codigo_like=%${value}%&titulo_like=%${value}%`;
    }

    this.setFilter(`${query}`);
    this.list();
  }
}

const dataTableStore = new DataTableStore();

export { dataTableStore };
